
import React from 'react';
import { usePersonalization } from '@/hooks/usePersonalization';
import { ErrorBoundary } from '@/components/ui/error-boundary';

interface DashboardSection {
  id: string;
  component: React.ReactNode;
  priority: number;
  requiredLevel?: 'beginner' | 'intermediate' | 'advanced';
  requiredGoals?: string[];
}

interface AdaptiveDashboardLayoutProps {
  sections: DashboardSection[];
}

export const AdaptiveDashboardLayout: React.FC<AdaptiveDashboardLayoutProps> = ({ sections }) => {
  const { config, profile, isLoading } = usePersonalization();


  // Don't render sections while personalization is loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Personalizing your dashboard...</p>
        </div>
      </div>
    );
  }

  const visibleSections = sections
    .filter(section => {
      // Filter by fitness level
      if (section.requiredLevel && profile?.fitness_level) {
        const levels = ['beginner', 'intermediate', 'advanced'];
        const userLevelIndex = levels.indexOf(profile.fitness_level);
        const requiredLevelIndex = levels.indexOf(section.requiredLevel);
        if (userLevelIndex < requiredLevelIndex) return false;
      }

      // Filter by required goals
      if (section.requiredGoals && profile?.primary_goals) {
        const hasRequiredGoal = section.requiredGoals.some(goal => 
          profile.primary_goals?.includes(goal)
        );
        if (!hasRequiredGoal) return false;
      }

      // Filter by hidden components - with defensive check
      if (config?.hiddenComponents && Array.isArray(config.hiddenComponents)) {
        if (config.hiddenComponents.includes(section.id)) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      // Sort by prioritized sections first - with defensive check
      const prioritizedSections = config?.prioritizedSections || [];
      const aPrioritized = prioritizedSections.indexOf(a.id);
      const bPrioritized = prioritizedSections.indexOf(b.id);
      
      if (aPrioritized !== -1 && bPrioritized !== -1) {
        return aPrioritized - bPrioritized;
      }
      if (aPrioritized !== -1) return -1;
      if (bPrioritized !== -1) return 1;
      
      // Then by priority number
      return a.priority - b.priority;
    });

  return (
    <ErrorBoundary>
      <div className="space-y-6 animate-fade-in">
        {visibleSections.map(section => (
          <ErrorBoundary key={section.id}>
            <div>
              {section.component}
            </div>
          </ErrorBoundary>
        ))}
      </div>
    </ErrorBoundary>
  );
};
